<template>
    <v-layout justify-center class="pa-4">
        <v-responsive width="100%" height="100%" max-width="1200">
            <list-heading title="나누리상품권 주문 매출" v-model="showsSearch" showsFilterButton />

            <nshop-viewer-statistics-search v-bind="{ showsSearch, loading }" />

            <v-row class="ma-n2 mt-n1">
                <v-col class="pa-2" cols="auto">
                    <list-widget title="추천인">
                        <b class="mr-1"> {{ (summary.totalCount || 0).format() }} </b> 개
                    </list-widget>
                </v-col>
                <v-col class="pa-2" cols="auto">
                    <list-widget title="총 매출금액">
                        <b class="mr-1"> {{ (summary.order?.sales || 0).format() }} </b> 원
                    </list-widget>
                </v-col>
                <v-col class="pa-2" cols="auto">
                    <list-widget title="총 주문건수">
                        <b class="mr-1"> {{ (summary.order?.count || 0).format() }} </b> 건
                    </list-widget>
                </v-col>
                <v-col class="pa-2" cols="auto">
                    <list-widget title="총 발송건수">
                        <b class="mr-1"> {{ (summary.message?.count || 0).format() }} </b> 건
                    </list-widget>
                </v-col>
                <v-col class="pa-2" cols="auto">
                    <nshop-order-list-pg />
                </v-col>
                <v-spacer />
                <v-col class="pa-2" cols="auto">
                    <list-excel v-slot="{ attrs, on }" v-on="{ excel }">
                        <v-card outlined style="overflow: hidden;">
                            <v-btn height="40" color="white" elevation="0" class="text-caption green--text" style="margin:-1px" v-bind="{ ...attrs, loading }" v-on="on">
                                <v-icon> mdi-microsoft-excel </v-icon>
                                <span> 엑셀다운로드 </span>
                            </v-btn>
                        </v-card>
                    </list-excel>
                </v-col>
                <v-col class="pa-2" cols="12">
                    <v-data-table v-bind="{ loading, headers, items }" disable-sort disable-pagination hide-default-footer class="v-sheet--outlined" :items-per-page="-1" ref="data-table" mobile-breakpoint="880">
                        <template v-for="(header, index) in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }"> <span :key="index" :title="header.withTitle ? header.formatter.bind(item)(value, item) : undefined" v-html="header.formatter.bind(item)(value, item)" /> </template>

                        <template #[`item.actions`]="{ item }">
                            <template v-if="item._id">
                                <v-btn x-small text tile class="pa-0 caption" title="해당 추천인의 주문 조회" :to="{ path: '/console/nshop/orders', query: { _viewer: item._id } }">
                                    <v-icon small>mdi-receipt-text</v-icon>
                                </v-btn>
                                <v-btn x-small text tile class="pa-0 caption" title="해당 추천인의 기간별 통계" :to="{ path: '/console/nshop/statistics', query: { _viewer: item._id } }">
                                    <v-icon small>mdi-chart-timeline-variant</v-icon>
                                </v-btn>
                            </template>
                            <span v-else>-</span>
                        </template>

                        <template #footer>
                            <v-divider />
                            <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-2" @input="(page) => $router.push({ query: { ...$route.query, page } })" />
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import { initDataTableHeaders } from "@/assets/lib";

import { initDuration } from "@/components/console/dumb/list/search-duration";
import ListExcel from "@/components/console/dumb/list/list-excel.vue";
import ListHeading from "@/components/console/dumb/list/list-heading.vue";
import ListWidget from "@/components/console/dumb/list/list-widget.vue";
import NshopOrderListPg from "@/components/console/nshop/orders/nshop-order-list-pg.vue";
import NshopViewerStatisticsSearch from "@/components/console/nshop/nshop-viewer-statistics-search.vue";

const headers = initDataTableHeaders([
    {
        text: "#",
        value: "index",
        width: +60,
        align: "end",
    },
    {
        text: "추천인코드",
        value: "viewer.refid",
        width: 160,
        // 추천인이 지정되지 않은 주문묶음은 _id 가 null 로 내려온다.
        // 뷰어 지정 이전에 발생한 주문은 소급 귀속하지 않는 것이 고객사 정책이라 정상적인 줄이다.
        // _id 는 있는데 조인된 계정이 없으면 그 사이 삭제된 추천인이다 — 실적은 그대로 남는다.
        formatter: (value, item) => {
            if (!item?._id) return "미지정";
            if (!item?.viewer?._id) return "삭제된 추천인";
            return value || "-";
        },
    },
    {
        text: "아이디",
        value: "viewer.username",
        width: 140,
    },
    {
        text: "담당자",
        value: "viewer.name",
        width: 100,
    },
    {
        text: "주문건수",
        value: "order.count",
        width: 110,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
    {
        text: "매출금액",
        value: "order.sales",
        width: 160,
        align: "end",
        formatterType: "number",
        formatterSuffix: "원",
    },
    {
        text: "발송건수",
        value: "message.count",
        width: 110,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
    {
        text: "알림톡",
        value: "message.kakao.count",
        width: 100,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
    {
        text: "대체문자",
        value: "message.plain.count",
        width: 100,
        align: "end",
        formatterType: "number",
        formatterSuffix: "건",
    },
    {
        text: "조회",
        value: "actions",
        width: +90,
        hideOnExcel: true,
        align: "center",
        class: "pa-0",
        cellClass: "pa-0",
    },
]);

export default {
    components: {
        ListExcel,
        ListHeading,
        ListWidget,
        NshopOrderListPg,
        NshopViewerStatisticsSearch,
    },
    data: () => ({
        statistics: [],

        limit: 20,
        summary: { totalCount: 0 },

        headers,

        loading: false,
        showsSearch: true,
    }),
    computed: {
        items() {
            // 매출 내림차순 정렬이므로 번호가 곧 순위다.
            return this.statistics.map((item, index) => ({
                ...item,
                index: this.skip + index + 1,
            }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;

            // 기간 미지정이면 이번 달 — 검색필터의 기본값과 같은 값을 쓴다.
            // 전체기간 집계를 기본으로 두면 화면에 뜬 기간과 숫자가 어긋난다.
            if (!Array.isArray(query["searchDateValue"]) || !query["searchDateValue"][0] || !query["searchDateValue"][1]) {
                query["searchDateValue"] = initDuration();
            }

            return { ...query };
        },
    },
    methods: {
        async init() {
            this.statistics = [];
            this.summary = { totalCount: 0 };
            this.loading = false;

            await this.search();
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;
            try {
                let { skip, limit, params } = this;
                let { summary, statistics } = await api.console.nshop.viewerStatistics.gets({
                    headers: { skip, limit },
                    params,
                });

                this.summary = summary;
                this.statistics = statistics;
            } finally {
                this.loading = false;
            }
        },

        async excel({ skip = 0, limit = 0 } = {}) {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { params } = this;
                let { statistics } = await api.console.nshop.viewerStatistics.gets({
                    headers: { skip, limit },
                    params,
                });

                let { headers } = this;
                let fileName = "나누리상품권_주문매출";
                this.$excel(
                    statistics.map((item, index) => ({ ...item, index: index + 1 })),
                    headers,
                    fileName,
                );
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        params() {
            this.search();
        },
    },
    mounted() {
        this.init();
    },
};
</script>

<style lang="scss" scoped></style>
